import useGet from '../../../core/composables/api/useGet'
import usePost from '../../../core/composables/api/usePost'
import { onMounted, ref } from 'vue'
import type { FeedbackTopicType } from '@/core/models/feedback-topic'
import type { Perspective } from '@/core/models/perspective'
import { useDialog } from '@/core/components/base/dialog/use-dialog'
import type { PulseAnalysis } from '@/core/models/surveys-analytics'
import type { SurveySelectionPreview } from './use-evaluation-profile'

export const usePulseProfile = (feedbackTopicId: string) => {
  const { data, status, load, reload } = useGet<PulseProfile>(`profile/pulse/${feedbackTopicId}`)

  const selectedSurveyIds = ref<string[]>([])

  const {
    data: analysisData,
    status: analysisStatus,
    mutate: loadAnalysis,
  } = usePost<PulseAnalysis, { surveyIds: string[] }>(`profile/pulse/${feedbackTopicId}/analysis`)

  onMounted(async () => {
    await load()
    if (status.value === 'success') {
      const allSurveyIds = data.value?.surveys.map((survey) => survey.id) ?? []
      selectedSurveyIds.value = allSurveyIds
      loadAnalysis({ surveyIds: allSurveyIds })
    }
  })

  const { openDialog, isOpen, closeDialog } = useDialog()

  const applySelectedSurveys = (surveyIds: string[]) => {
    closeDialog()
    selectedSurveyIds.value = [...surveyIds]
    loadAnalysis({ surveyIds: selectedSurveyIds.value })
  }

  return {
    data,
    status,
    load,
    reload,
    analysisData,
    analysisStatus,
    applySelectedSurveys,
    openDialog,
    isOpen,
    closeDialog,
    selectedSurveyIds,
  }
}

export interface PulseProfile {
  readonly title: string
  readonly id: string
  readonly type: FeedbackTopicType
  readonly icon?: string
  readonly perspectives: ReadonlyArray<Perspective>
  readonly includeSelf: boolean
  readonly surveys: ReadonlyArray<SurveySelectionPreview>
}
